import { Fragment, useEffect, useState } from "react";
import "./CostBandTable.css";
import CalculationPopup from "./CalculationPopup";
import { SYNTHETIC_FIELD_NEEDLES, syntheticReasonText } from "./syntheticFields";

const fmt = (n) => "£" + Math.round(n).toLocaleString("en-GB");
const fmtInt = (n) => Math.round(n).toLocaleString("en-GB");

// Per-field calculator icon. Ported from old Angular's
// non-core-compensation.component.html: `<i class="fa-solid fa-calculator">`
// rendered next to the NTR/Exp/Vexp value ONLY when
// getSyntheticTooltip(item.syntheticData, 'l1'/'l2'/'l3') is truthy for that
// specific field (per-field, not the row-level `synthetic` boolean) AND
// !item.isBlended - a territory-level flag (territory.is_blended, e.g. South
// Africa/Romania are blended-source territories) that suppresses the icon
// entirely regardless of synthetic_data. FIXED 2026-08-05: the initial
// version of this icon was missing that isBlended gate (routes.py wasn't
// forwarding is_blended at all - see cost_band_detail.sql/routes.py), which
// showed the icon on blended territories old never shows it on - callers
// below now pass `!d.is_blended && ...` as the reason. This app doesn't
// load Font Awesome anywhere (confirmed via grep - no fa- classes
// referenced), so this is a small inline SVG standing in for that icon
// instead. The number itself stays clickable regardless of synthetic/
// blended status either way - unchanged.
// Ported from non-core-compensation.component.html's `@if(showSuperScript &&
// !isTerritoryOverridden(item.territoryId)){ <sup>+{{item.incentiveL1||0}}%</sup> }`.
// showSuperScript is old's own per-load flag, set true only when the active
// Vertical Stack is Sales (verticalStackId === 7, confirmed via a live DB
// lookup) - passed in here as the `show` prop instead of being recomputed
// per-cell. Renders "+0%" rather than nothing when the value is missing,
// matching old's `|| 0` fallback exactly.
function IncentiveTag({ value }) {
  return (
    <sup className="incentive-sup" title="incentive">
      +{value || 0}%
    </sup>
  );
}

function CalcFieldIcon({ reason }) {
  if (!reason) return null;
  return (
    <svg
      className="calc-field-icon"
      viewBox="0 0 16 16"
      width="12"
      height="12"
      aria-hidden="true"
      title={reason}
    >
      <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <rect x="3.5" y="3.3" width="9" height="2.6" fill="currentColor" />
      <circle cx="4.6" cy="8.4" r="0.9" fill="currentColor" />
      <circle cx="8" cy="8.4" r="0.9" fill="currentColor" />
      <circle cx="11.4" cy="8.4" r="0.9" fill="currentColor" />
      <circle cx="4.6" cy="11.4" r="0.9" fill="currentColor" />
      <circle cx="8" cy="11.4" r="0.9" fill="currentColor" />
      <circle cx="11.4" cy="11.4" r="0.9" fill="currentColor" />
    </svg>
  );
}

// Sequential green scale for cost delta vs UK - do not switch to red/green,
// see dashboard reference README "Visual design principles".
function deltaClass(pct) {
  if (pct >= 0) return "delta-neutral";
  if (pct > -10) return "delta-b1";
  if (pct > -20) return "delta-b2";
  if (pct > -30) return "delta-b3";
  if (pct > -40) return "delta-b4";
  return "delta-b5";
}

// FIXED 2026-08-05: sorting by territory (UK, Ireland, South Africa,
// Romania, India, Brazil) now happens once in JobDetailTerritoryQuarterRow.jsx
// before the `data` prop reaches this component - no longer needed here.

function DeltaTag({ val, refVal, isRef }) {
  if (isRef) return <span className="delta delta-ref">ref</span>;
  // Same guard as the old Angular ComparisionPercentageDiffPipe: no reference
  // value, or a zero reference (would divide by zero), renders nothing.
  if (refVal === 0 || refVal == null || val == null) return null;
  const pct = ((val - refVal) / refVal) * 100;
  const sign = pct >= 0 ? "+" : "";
  return <span className={`delta ${deltaClass(pct)}`}>{sign}{pct.toFixed(0)}%</span>;
}

// EE count cell. Old Angular (non-core-compensation.component.html) always
// renders the real number here regardless of the row's `synthetic` flag -
// synthetic only affects the pay bands themselves, not the reported EE
// counts - so this port no longer suppresses the number for synthetic rows.
// Only a genuinely missing value (null/undefined) falls back to "-".
function eeCell(value, lowN, lowNTitle) {
  if (value == null) return "-";
  return lowN
    ? <span className="low-n-val" title={lowNTitle}>{fmtInt(value)}</span>
    : fmtInt(value);
}

// FIXED 2026-08-05 (3rd territory/quarter attempt): data/loading/error are
// now plain props from App.js instead of an internal fetch. Checked old
// code directly - compensation-range-chart.component.ts (the real Range
// Bars chart) has NO HTTP call of its own at all, just `@Input()
// territoryData` - this table and that chart are both fed by the SAME
// single fetch in non-core-compensation.component.ts's loadData(). This
// port previously had this table, RangeBarsChart.jsx, AND
// DistributionChart.jsx each independently re-fetching the same data keyed
// off Territory/Quarter - three redundant fetches racing each other
// whenever those reset (confirmed as the real root cause of the stale-
// territory bug, not a missing guard - old never has more than one fetch in
// flight for this data to race against). The one fetch now lives in
// JobDetailTerritoryQuarterRow.jsx (see App.js), matching old's loadData().
export default function CostBandTable({ filters, data, loading, error, onTerritoryExpand }) {
  // Reference territory for the % delta columns. Defaults to United Kingdom
  // (territory_id from this response), same as old Minerva's
  // setComparisionForGivenTerritory(4) hardcoded default. Toggled by clicking
  // a territory's flag; clicking the active flag again sets this back to
  // null, which hides every % (matches toggleBaseComparison's clear path).
  const [referenceId, setReferenceId] = useState(null);

  // Region accordion (ported from non-core-compensation.component.ts's
  // expandedTerritories/regionDataMap/expandedSourceRegions/regionComparisonDataMap).
  // Old Minerva only ever allows one territory and one region open at a
  // time - not a per-row map of booleans - so a single id does the same job.
  const [expandedTerritoryId, setExpandedTerritoryId] = useState(null);
  const [regionsByTerritory, setRegionsByTerritory] = useState({}); // cache, keyed by territory_id
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [expandedRegionId, setExpandedRegionId] = useState(null);
  const [comparisonsByRegion, setComparisonsByRegion] = useState({}); // cache, keyed by region_id
  const [comparisonsLoading, setComparisonsLoading] = useState(false);

  // Notify the parent (App.js) which territory's accordion is open, so
  // RegionMap.jsx - a sibling, not a child, of this table - can be driven
  // by it. Ports old Angular's real behavior: region-osm-view is driven by
  // selectedTerritoryId, set when a territory's row is expanded here
  // (toggleAccordion), not by the top Territory filter.
  useEffect(() => {
    onTerritoryExpand?.(expandedTerritoryId);
  }, [expandedTerritoryId, onTerritoryExpand]);

  // Region promote/reset (ported from non-core-compensation.component.ts's
  // onRegionNameClick()/resetTerritoryOverride()/getDisplayData()). Old
  // Angular stores a full override record per territory and merges it into
  // whatever renders that row; we do the same, keyed by territory_id, and
  // derive displayData below by overlaying it onto the base row. Promoting
  // collapses the accordion (matches the old code's expandedTerritories[...]
  // = false), same as picking a plain territory row would look afterward.
  const [regionOverrides, setRegionOverrides] = useState({}); // keyed by territory_id

  const promoteRegion = (territoryId, regionName, l1, l2, l3, baseL1, baseL2, baseL3) => {
    setRegionOverrides((prev) => ({
      ...prev,
      [territoryId]: { regionName, ntr: l1, exp: l2, vexp: l3, base_ntr: baseL1, base_exp: baseL2, base_vexp: baseL3 },
    }));
    setExpandedTerritoryId(null);
    setExpandedRegionId(null);
  };

  const resetOverride = (territoryId) => {
    setRegionOverrides((prev) => {
      if (!(territoryId in prev)) return prev;
      const next = { ...prev };
      delete next[territoryId];
      return next;
    });
  };

  // Fully Inclusive Cost popup. Ported from the real calculation(baseAmount,
  // country, territoryId, amount, label, fullLabel, event, regionId) click
  // handler - our version only needs the fields the backend routes actually
  // use (territoryId, timePeriodId, baseAmount, jobTitleId), plus a label
  // for the popup's subtitle.
  const [popupData, setPopupData] = useState(null);
  const openCalculation = (territoryId, label, baseAmount, regionId) => {
    setPopupData({
      territoryId,
      timePeriodId,
      baseAmount,
      jobTitleId,
      label,
      regionId,
    });
  };

  // Map FilterBar's selection object (verticalStack/subStack/jobTitle/quarter)
  // to what the region-accordion/calculation-popup secondary fetches below
  // still need directly.
  const jobTitleId = filters?.jobTitle?.id;
  const timePeriodId = filters?.quarter?.id;

  // Ported from non-core-compensation.component.ts's loadData():
  // `if (param.verticalStackId === 7) { this.showSuperScript = true; }` -
  // verticalStackId there is Config.stackModel.verticalStackId, the same
  // source as our own filters.verticalStack.id (FilterBar's selection).
  // 7 = Sales, confirmed via a live DB lookup.
  const showIncentive = filters?.verticalStack?.id === 7;

  // FIXED 2026-08-05: `data` is now a prop (see the note above the component
  // signature) - this just does what old's loadData() does every time it
  // re-fetches: reset the region caches/overrides and re-pick the default
  // reference territory, now keyed off the `data` prop reference (a new
  // array from JobDetailTerritoryQuarterRow.jsx every time it fetches)
  // instead of a local fetch's own `.then()`.
  useEffect(() => {
    const uk = data.find((r) => r.territory_id === 4);
    setReferenceId(uk ? uk.territory_id : null);
    setExpandedTerritoryId(null);
    setRegionsByTerritory({});
    setExpandedRegionId(null);
    setComparisonsByRegion({});
    setRegionOverrides({});
  }, [data]);

  // Overlay any active region override onto its territory's row - ported
  // from getDisplayData(item) in non-core-compensation.component.ts. Only
  // the NTR/Exp/Vexp benchmark cells and the displayed name change; Min/Max/
  // spreads/EE counts stay the territory-level figures since a single region
  // doesn't have its own.
  const displayData = data.map((d) => {
    const override = regionOverrides[d.territory_id];
    if (!override) return d;
    return { ...d, ...override, isRegionOverride: true };
  });

  const refRow = displayData.find((d) => d.territory_id === referenceId);

  const toggleReference = (territoryId) => {
    setReferenceId((prev) => (prev === territoryId ? null : territoryId));
  };

  // Ported from toggleAccordion(item) in non-core-compensation.component.ts.
  // Only one territory's regions can be open at a time (old code force-closes
  // every other key in expandedTerritories before opening the clicked one).
  // Fetch only runs once per territory_id - old code's own cache check
  // (`&& !this.regionDataMap[...]`) - so re-opening a territory already
  // fetched this session is instant, no repeat network call.
  const toggleTerritoryAccordion = (territoryId) => {
    setExpandedRegionId(null);
    if (expandedTerritoryId === territoryId) {
      setExpandedTerritoryId(null);
      return;
    }
    setExpandedTerritoryId(territoryId);
    if (regionsByTerritory[territoryId]) return;
    setRegionsLoading(true);
    const params = new URLSearchParams({
      job_title_id: jobTitleId,
      territory_id: territoryId,
      time_period_id: timePeriodId,
    });
    fetch(`/api/territory-region-source?${params.toString()}`)
      .then((res) => res.json())
      .then((regions) => {
        setRegionsByTerritory((prev) => ({ ...prev, [territoryId]: regions }));
        setRegionsLoading(false);
      })
      .catch(() => setRegionsLoading(false));
  };

  // Ported from toggleSourceRegion(regionId) in non-core-compensation.component.ts.
  // NOTE: the real template calls this with region.regionId to expand but
  // region.territoryRegionSourceId to collapse (two different fields) - that
  // looks like a bug in the old code, not an intentional distinction, so this
  // port uses region_id consistently for both directions instead of copying
  // the mismatch. Flagging this as a deliberate deviation, not a guess.
  const toggleSourceRegion = (regionId) => {
    if (expandedRegionId === regionId) {
      setExpandedRegionId(null);
      return;
    }
    setExpandedRegionId(regionId);
    if (comparisonsByRegion[regionId]) return;
    setComparisonsLoading(true);
    const params = new URLSearchParams({
      job_title_id: jobTitleId,
      territory_id: expandedTerritoryId,
      time_period_id: timePeriodId,
      base_territory_region_id: regionId,
    });
    fetch(`/api/compensation-by-territory-region?${params.toString()}`)
      .then((res) => res.json())
      .then((comparisons) => {
        setComparisonsByRegion((prev) => ({ ...prev, [regionId]: comparisons }));
        setComparisonsLoading(false);
      })
      .catch(() => setComparisonsLoading(false));
  };

  return (
    <section className="cbd-section">
      <div className="cbd-section-head">
        <h2 className="cbd-title">Cost band detail</h2>
        <span className="cbd-spacer"></span>
        <span className="cbd-aside">All figures GBP · territories in operational order</span>
      </div>

      <div className="cbd-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Territory</th>
              <th className="col-min">Min</th>
              <th>NTR</th>
              <th>Exp</th>
              <th className="col-vexp">Vexp</th>
              <th>Max</th>
              <th>Core band spread</th>
              <th>Full range spread</th>
              <th className="col-n">Core band EE count</th>
              <th className="col-n">Full range EE count</th>
            </tr>
          </thead>
          <tbody>
            {!jobTitleId && (
              <tr><td colSpan={10} className="cbd-status">Select a job title to see cost band detail.</td></tr>
            )}
            {jobTitleId && loading && (
              <tr><td colSpan={10} className="cbd-status">Loading…</td></tr>
            )}
            {jobTitleId && !loading && error && (
              <tr><td colSpan={10} className="cbd-status">Couldn't load data.</td></tr>
            )}
            {jobTitleId && !loading && !error && displayData.length === 0 && (
              <tr><td colSpan={10} className="cbd-status">No data for this selection.</td></tr>
            )}
            {jobTitleId && !loading && !error && displayData.map((d, i) => {
              const isReference = d.territory_id === referenceId;
              const fullNCell = eeCell(
                d.full_range_ee_count,
                d.low_n,
                "Sample size below 30: limited statistical reliability"
              );
              const coreNCell = eeCell(
                d.core_band_ee_count,
                d.low_n,
                "Derived from a sample below 30: limited statistical reliability"
              );
              const isExpanded = expandedTerritoryId === d.territory_id;
              const regions = regionsByTerritory[d.territory_id];

              return (
                <Fragment key={d.territory_id}>
                <tr style={{ animationDelay: `${i * 60 + 200}ms` }}>
                  <td className="terr-cell">
                    <div className="terr-cell-main">
                      <button
                        type="button"
                        className="region-toggle"
                        onClick={() => toggleTerritoryAccordion(d.territory_id)}
                        title={expandedTerritoryId === d.territory_id ? "Hide regions" : "Show regions"}
                        aria-expanded={expandedTerritoryId === d.territory_id}
                      >{expandedTerritoryId === d.territory_id ? "▾" : "▸"}</button>
                      <span className="terr-name">
                        {d.territory}
                        {d.isRegionOverride && (
                          <>
                            <span className="region-override-badge" title={`Showing ${d.regionName} instead of the territory average`}>
                              {d.regionName}
                            </span>
                            <button
                              type="button"
                              className="region-override-reset"
                              onClick={() => resetOverride(d.territory_id)}
                              title="Reset to territory average"
                            >↺</button>
                          </>
                        )}
                        {/* FIXED 2026-08-05: was matching on `d.territory === "South Africa"` -
                            the API returns the raw DB name (e.g. "SOUTH AFRICA", matching old
                            Angular's Config.territories `name` key, not its display-only `label`),
                            so this never actually matched. territory_id 2 = South Africa. */}
                        {d.territory_id === 2 && <sup className="schema-mark" title="Schema varies: see note below">ⁿ</sup>}
                        {d.synthetic && <sup className="schema-mark synthetic" title="Synthetic bands: no incumbent data">ˢ</sup>}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={`ref-flag${isReference ? " active" : ""}`}
                      onClick={() => toggleReference(d.territory_id)}
                      title={isReference ? "Reference territory: click to clear" : "Set as reference territory"}
                      aria-pressed={isReference}
                    >⚑</button>
                  </td>
                  <td className="num min col-min">{fmt(d.min)}</td>
                  <td className="num bench-col">
                    <span className="calc-trigger" onClick={() => openCalculation(d.territory_id, `${d.territory} · NTR`, d.base_ntr)} title="View Fully Inclusive Cost breakdown">
                      {fmt(d.ntr)}
                    </span>
                    {showIncentive && !d.isRegionOverride && <IncentiveTag value={d.incentive_l1} />}
                    <CalcFieldIcon reason={!d.is_blended && syntheticReasonText(d.synthetic_data, SYNTHETIC_FIELD_NEEDLES.ntr)} />
                    {refRow && <DeltaTag val={d.ntr} refVal={refRow.ntr} isRef={isReference} />}
                  </td>
                  <td className="num median bench-col">
                    <span className="calc-trigger" onClick={() => openCalculation(d.territory_id, `${d.territory} · Exp`, d.base_exp)} title="View Fully Inclusive Cost breakdown">
                      {fmt(d.exp)}
                    </span>
                    {showIncentive && !d.isRegionOverride && <IncentiveTag value={d.incentive_l2} />}
                    <CalcFieldIcon reason={!d.is_blended && syntheticReasonText(d.synthetic_data, SYNTHETIC_FIELD_NEEDLES.exp)} />
                    {refRow && <DeltaTag val={d.exp} refVal={refRow.exp} isRef={isReference} />}
                  </td>
                  <td className="num bench-col col-vexp">
                    <span className="calc-trigger" onClick={() => openCalculation(d.territory_id, `${d.territory} · Vexp`, d.base_vexp)} title="View Fully Inclusive Cost breakdown">
                      {fmt(d.vexp)}
                    </span>
                    {showIncentive && !d.isRegionOverride && <IncentiveTag value={d.incentive_l3} />}
                    <CalcFieldIcon reason={!d.is_blended && syntheticReasonText(d.synthetic_data, SYNTHETIC_FIELD_NEEDLES.vexp)} />
                    {refRow && <DeltaTag val={d.vexp} refVal={refRow.vexp} isRef={isReference} />}
                  </td>
                  <td className="num max">{fmt(d.max)}</td>
                  <td className="num">{fmt(d.core_band_spread)}</td>
                  <td className="num">{fmt(d.full_range_spread)}</td>
                  <td className="num col-n">{coreNCell}</td>
                  <td className="num col-n">{fullNCell}</td>
                </tr>
                {isExpanded && (
                <tr>
                  <td colSpan={10} className="region-accordion-cell">
                    {regionsLoading && !regions && <div className="cbd-status region-status">Loading regions…</div>}
                    {!regionsLoading && regions && regions.length === 0 && (
                      <div className="cbd-status region-status">No region data found.</div>
                    )}
                    {regions && regions.length > 0 && (
                      <table className="region-table">
                        <thead>
                          <tr>
                            <th>Region</th>
                            <th>New to Role (NTR)</th>
                            <th>Experienced (Exp)</th>
                            <th>V-Experienced (Vexp)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {regions.map((r) => {
                            const isRegionOpen = expandedRegionId === r.region_id;
                            const comparisons = comparisonsByRegion[r.region_id];
                            return (
                              <Fragment key={r.region_id}>
                                <tr className="region-row">
                                  <td className="region-name-cell">
                                    <button
                                      type="button"
                                      className="region-toggle"
                                      onClick={() => toggleSourceRegion(r.region_id)}
                                      title={isRegionOpen ? "Hide comparison regions" : "Show comparison regions"}
                                      aria-expanded={isRegionOpen}
                                    >{isRegionOpen ? "▾" : "▸"}</button>
                                    {r.region_name}
                                    <button
                                      type="button"
                                      className="region-promote"
                                      title={`Use ${r.region_name} for ${d.territory}`}
                                      onClick={() => promoteRegion(d.territory_id, r.region_name, r.full_l1, r.full_l2, r.full_l3, r.base_l1, r.base_l2, r.base_l3)}
                                    >⇪</button>
                                  </td>
                                  <td className="num">
                                    <span className="calc-trigger" onClick={() => openCalculation(d.territory_id, `${r.region_name} · NTR`, r.base_l1, r.territory_region_source_id)} title="View Fully Inclusive Cost breakdown">
                                      {fmt(r.full_l1)}
                                    </span>
                                    {refRow && <DeltaTag val={r.full_l1} refVal={refRow.ntr} isRef={false} />}
                                  </td>
                                  <td className="num">
                                    <span className="calc-trigger" onClick={() => openCalculation(d.territory_id, `${r.region_name} · Exp`, r.base_l2, r.territory_region_source_id)} title="View Fully Inclusive Cost breakdown">
                                      {fmt(r.full_l2)}
                                    </span>
                                    {refRow && <DeltaTag val={r.full_l2} refVal={refRow.exp} isRef={false} />}
                                  </td>
                                  <td className="num">
                                    <span className="calc-trigger" onClick={() => openCalculation(d.territory_id, `${r.region_name} · Vexp`, r.base_l3, r.territory_region_source_id)} title="View Fully Inclusive Cost breakdown">
                                      {fmt(r.full_l3)}
                                    </span>
                                    {refRow && <DeltaTag val={r.full_l3} refVal={refRow.vexp} isRef={false} />}
                                  </td>
                                </tr>
                                {isRegionOpen && (
                                  <tr key={`compare-${r.region_id}`}>
                                    <td colSpan={4} className="comparison-cell">
                                      {comparisonsLoading && !comparisons && (
                                        <div className="cbd-status region-status">Loading comparison regions…</div>
                                      )}
                                      {!comparisonsLoading && comparisons && comparisons.length === 0 && (
                                        <div className="cbd-status region-status">No comparison data found.</div>
                                      )}
                                      {comparisons && comparisons.length > 0 && (
                                        <table className="comparison-table">
                                          <thead>
                                            <tr>
                                              <th>Comparison region</th>
                                              <th>New to Role (NTR)</th>
                                              <th>Experienced (Exp)</th>
                                              <th>V-Experienced (Vexp)</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {comparisons.map((c) => (
                                              <tr key={c.comparision_territory_region_id}>
                                                <td className="region-name-cell">{c.comparision_region_name}</td>
                                                <td className="num">
                                                  <span className="calc-trigger" onClick={() => openCalculation(c.territory_id, `${c.comparision_region_name} · NTR`, c.base_l1, r.territory_region_source_id)} title="View Fully Inclusive Cost breakdown">
                                                    {fmt(c.full_l1)}
                                                  </span>
                                                  {refRow && <DeltaTag val={c.full_l1} refVal={refRow.ntr} isRef={false} />}
                                                </td>
                                                <td className="num">
                                                  <span className="calc-trigger" onClick={() => openCalculation(c.territory_id, `${c.comparision_region_name} · Exp`, c.base_l2, r.territory_region_source_id)} title="View Fully Inclusive Cost breakdown">
                                                    {fmt(c.full_l2)}
                                                  </span>
                                                  {refRow && <DeltaTag val={c.full_l2} refVal={refRow.exp} isRef={false} />}
                                                </td>
                                                <td className="num">
                                                  <span className="calc-trigger" onClick={() => openCalculation(c.territory_id, `${c.comparision_region_name} · Vexp`, c.base_l3, r.territory_region_source_id)} title="View Fully Inclusive Cost breakdown">
                                                    {fmt(c.full_l3)}
                                                  </span>
                                                  {refRow && <DeltaTag val={c.full_l3} refVal={refRow.vexp} isRef={false} />}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        <p className="cbd-footnote">
          <sup className="schema-mark">ⁿ</sup> Schema varies: South Africa uses P10 / P30 / P40 / P50 / P90 for the Min / NTR / Exp / Vexp / Max tiers respectively. All other territories follow the standard P10 / P40 / P50 / P60 / P90 mapping.&nbsp;
          <sup className="schema-mark synthetic">ˢ</sup> Synthetic bands are derived from approved business rules rather than reported incumbent data. Range and tier values are illustrative; no statistical distribution is inferred.&nbsp;
          <sup className="schema-mark low-n-mark">*</sup> Sample size below n=30: figures are indicative; statistical inference should be treated with caution.&nbsp;
          <strong className="footnote-mark">%</strong> NTR / Exp / Vexp tiers show cost delta vs the flagged reference territory (United Kingdom by default; click any territory's <span className="ref-flag active" style={{ pointerEvents: "none" }}>⚑</span> to compare against it instead), on a sequential scale. <span className="fn-b5">Deeper greens flag the strongest business case</span> for distributed hiring (greater savings); paler greens carry modest savings; <span className="fn-neutral">grey marks territories that cost more than the reference</span> (weaker case).&nbsp;
          <strong className="footnote-mark">†</strong> Core band EE count is derived from the full range EE count, representing the share of employees whose pay sits within the NTR–Vexp percentile span (~20%); not separately reported by the data source.
        </p>
      </div>

      <div className="method-callout">
        <div className="method-callout-head">
          <span className="method-callout-icon" aria-hidden="true">i</span>
          <span className="method-callout-title">Reading the percentiles</span>
        </div>
        <div className="method-callout-body">
          Percentiles describe where each pay tier sits within a territory's incumbent population. <strong>P50 is the median</strong>: half of employees earn less, half earn more. <strong>P10</strong> sits near the bottom of the pay range; <strong>P90</strong> near the top. Our three benchmark tiers map onto this:
          <span className="method-tier"><span className="method-tier-mark">NTR</span><span className="method-tier-pct">P40</span> just below the median: typical pay for new joiners</span>
          <span className="method-tier"><span className="method-tier-mark">Exp</span><span className="method-tier-pct">P50</span> the median: typical pay for experienced incumbents</span>
          <span className="method-tier"><span className="method-tier-mark">Vexp</span><span className="method-tier-pct">P60</span> just above the median: typical pay for very experienced staff</span>
          <span className="method-callout-note">South Africa's schema is shifted (P30/P40/P50 rather than P40/P50/P60), reflecting its market-positioning convention. The underlying interpretation is unchanged: NTR sits below typical, Exp at typical, Vexp above typical for that territory.</span>
        </div>
      </div>

      {popupData && <CalculationPopup data={popupData} onClose={() => setPopupData(null)} />}
    </section>
  );
}
